/**
 * Consent Management System
 *
 * Manages user consent for GDPR/CCPA compliance.
 * Integrates with cookie-banner-finally and other cookie consent tools.
 */

'use client';

import type { ConsentState } from './types';
import {
  COOKIE_CONSENT_KEY,
  DEFAULT_CONSENT,
  parseConsentCookieValue,
} from './consent-shared';

// Extend window for consent debug flag
declare global {
  interface Window {
    __consentDebugLogged?: boolean;
  }
}

/**
 * Cookie utility functions
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

function setCookie(name: string, value: string, days: number = 365): void {
  if (typeof document === 'undefined') return;

  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
}

/**
 * Get current consent state from cookie-banner-finally cookies
 */
export function getConsentState(): ConsentState {
  if (typeof window === 'undefined') {
    return DEFAULT_CONSENT;
  }

  try {
    const cookieValue = getCookie(COOKIE_CONSENT_KEY);
    return parseConsentCookieValue(cookieValue);
  } catch (error) {
    console.error('Failed to get consent state:', error);
  }

  return DEFAULT_CONSENT;
}

/**
 * Update consent state in cookie-banner-finally format
 */
export function setConsentState(consent: Partial<ConsentState>): void {
  if (typeof window === 'undefined') return;

  try {
    const current = getConsentState();
    const updated: ConsentState = {
      ...current,
      ...consent,
      necessary: true, // Always true
      timestamp: new Date(),
    };

    // Save to cookie in the same format as cookie-banner-finally
    const cookieValue = encodeURIComponent(JSON.stringify({
      necessary: updated.necessary,
      analytics: updated.analytics,
      marketing: updated.marketing,
      preferences: updated.preferences,
      timestamp: updated.timestamp?.toISOString() || new Date().toISOString(),
    }));

    setCookie(COOKIE_CONSENT_KEY, cookieValue);

    // Dispatch event for listeners
    window.dispatchEvent(
      new CustomEvent('consentUpdated', { detail: updated }),
    );

    console.log('Consent updated:', updated);
  } catch (error) {
    console.error('Failed to set consent state:', error);
  }
}

/**
 * Grant all consent categories
 */
export function grantAllConsent(): void {
  setConsentState({
    analytics: true,
    marketing: true,
    preferences: true,
  });
}

/**
 * Revoke all consent categories (except necessary)
 */
export function revokeAllConsent(): void {
  setConsentState({
    analytics: false,
    marketing: false,
    preferences: false,
  });
}

/**
 * Check if a specific consent category is granted
 */
export function hasConsent(category: keyof ConsentState): boolean {
  const consent = getConsentState();
  return consent[category] === true;
}

/**
 * Listen for consent changes
 */
export function onConsentChange(
  callback: (consent: ConsentState) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<ConsentState>;
    callback(customEvent.detail);
  };

  window.addEventListener('consentUpdated', handler);

  return () => {
    window.removeEventListener('consentUpdated', handler);
  };
}

/**
 * Integration with Cookiebot (if available)
 * @see https://www.cookiebot.com/en/developer/
 */
export function syncWithCookiebot(): void {
  if (typeof window === 'undefined') return;

  const checkCookiebot = () => {
    // @ts-ignore - Cookiebot global
    if (window.Cookiebot) {
      // @ts-ignore
      const cookiebot = window.Cookiebot;

      setConsentState({
        necessary: cookiebot.consent.necessary,
        analytics: cookiebot.consent.statistics,
        marketing: cookiebot.consent.marketing,
        preferences: cookiebot.consent.preferences,
      });

      // Listen for Cookiebot consent changes
      window.addEventListener('CookiebotOnAccept', () => {
        setConsentState({
          necessary: cookiebot.consent.necessary,
          analytics: cookiebot.consent.statistics,
          marketing: cookiebot.consent.marketing,
          preferences: cookiebot.consent.preferences,
        });
      });

      window.addEventListener('CookiebotOnDecline', () => {
        revokeAllConsent();
      });
    }
  };

  // Check immediately and after DOMContentLoaded
  checkCookiebot();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkCookiebot);
  }
}

/**
 * Integration with OneTrust (if available)
 * @see https://developer.onetrust.com/
 */
export function syncWithOneTrust(): void {
  if (typeof window === 'undefined') return;

  const checkOneTrust = () => {
    // @ts-ignore - OneTrust global
    if (window.OneTrust || window.OnetrustActiveGroups) {
      // @ts-ignore
      const groups = window.OnetrustActiveGroups || '';

      setConsentState({
        necessary: true, // Always allowed
        analytics: groups.includes('C0002'), // Performance Cookies
        marketing: groups.includes('C0004'), // Targeting Cookies
        preferences: groups.includes('C0003'), // Functional Cookies
      });

      // Listen for OneTrust consent changes
      // @ts-ignore
      window.addEventListener('consent.onetrust', () => {
        // @ts-ignore
        const groups = window.OnetrustActiveGroups || '';
        setConsentState({
          analytics: groups.includes('C0002'),
          marketing: groups.includes('C0004'),
          preferences: groups.includes('C0003'),
        });
      });
    }
  };

  checkOneTrust();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkOneTrust);
  }
}

/**
 * Integration with cookie-banner-finally
 * Listens for consent changes from the cookie banner and syncs with tracking system
 */
export function syncWithCookieBannerFinally(): void {
  if (typeof window === 'undefined') return;

  // Listen for cookie changes from cookie-banner-finally
  const checkCookieChanges = () => {
    try {
      const cookieValue = getCookie(COOKIE_CONSENT_KEY);
      if (cookieValue) {
        // Dispatch event to notify tracking system
        window.dispatchEvent(
          new CustomEvent('consentUpdated', {
            detail: parseConsentCookieValue(cookieValue),
          }),
        );
      }
    } catch (error) {
      console.error('Failed to sync with cookie-banner-finally:', error);
    }
  };

  // Check immediately
  checkCookieChanges();

  // Listen for storage events (when cookies are updated)
  window.addEventListener('storage', checkCookieChanges);

  // Also poll periodically as a fallback (every 1 second)
  setInterval(checkCookieChanges, 1000);
}

/**
 * Auto-detect and sync with available consent management platform
 */
export function autoSyncConsent(): void {
  syncWithCookieBannerFinally();
  syncWithCookiebot();
  syncWithOneTrust();
}

/**
 * Get consent state for server-side tracking
 * Returns only analytics and marketing consent (needed for server-side)
 *
 * @example
 * ```ts
 * 'use server';
 * import { trackEventServerSide } from '@/lib/tracking/server';
 * import { getConsentForServer } from '@/lib/tracking/consent';
 *
 * export async function handleOrder() {
 *   const consent = getConsentForServer(); // Called on client
 *   await trackEventServerSide(event, consent);
 * }
 * ```
 */
export function getConsentForServer(): {
  analytics?: boolean;
  marketing?: boolean;
} {
  const state = getConsentState();
  return {
    analytics: state.analytics,
    marketing: state.marketing,
  };
}
